/**
 * Navbar Component - Main navigation with WebSocket notifications
 * Extracted from App.js for better maintainability
 * 
 * This component receives auth data as props from App.js which manages the AuthContext
 */
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { getMediaUrl } from "../../utils";
import { haptic } from "../../utils/nativeBridge";
import feedback from "../../utils/notificationFeedback";
import { 
  Menu, X, Wallet, Zap, ArrowLeftRight, User, Gift, DollarSign, 
  Package, LogOut, Shield, Trophy, Bell, Check, Trash2, Lock, Crown, Layers,
  FileText, AlertTriangle, Activity, Users, Rocket, Gem, ShieldCheck, Radio, PlayCircle, Play, ChevronDown, ScanLine, Calculator
} from "lucide-react";

// Import price ticker
import { CompactPriceTicker } from "../common/AnimatedPriceTicker";
import ConfirmModal from "../ui/ConfirmModal";
import LegendaryLiveFlash from "../common/LegendaryLiveFlash";
import AppVersionBadge from "../common/AppVersionBadge";
import GetAppButton from "../common/GetAppButton";
import StoriesRing from "../common/StoriesRing";
import { BACKEND_URL } from "../../utils";

// API Configuration
const API = `${BACKEND_URL}/api`;

// Helper function for time-based greeting in PST
const getTimeBasedGreeting = () => {
  // Convert to PST (America/Los_Angeles)
  const pstTime = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", hour12: false });
  const hour = parseInt(pstTime, 10);
  if (hour >= 5 && hour < 12) {
    return "Good Morning";
  } else if (hour >= 12 && hour < 17) {
    return "Good Afternoon";
  } else {
    return "Good Evening";
  }
};

const getPSTTime = () => {
  return new Date().toLocaleString("en-US", { 
    timeZone: "America/Los_Angeles", 
    hour: "numeric", 
    minute: "2-digit", 
    hour12: true 
  }) + " PST";
};

// Device-level notification memory — ids this device has read/dismissed
// live in localStorage so a stale backend can NEVER resurrect them here.
const NOTIF_LS_KEY = "blk-notif-state";
const loadNotifLS = () => {
  try {
    const s = JSON.parse(localStorage.getItem(NOTIF_LS_KEY));
    return { dismissed: s?.dismissed || [], read: s?.read || [] };
  } catch { return { dismissed: [], read: [] }; }
};
const lsRememberNotif = (kind, ids) => {
  try {
    const s = loadNotifLS();
    s[kind] = Array.from(new Set([...s[kind], ...ids])).slice(-400);
    localStorage.setItem(NOTIF_LS_KEY, JSON.stringify(s));
  } catch { /* private mode */ }
};

const Navbar = ({ user, logout, token }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [newNotificationToast, setNewNotificationToast] = useState(null);
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const [, setTimeUpdate] = useState(0); // Forces re-render for live PST clock
  // Sign-out confirmation guard — protects against accidental taps mid-game.
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // Red dot on Locker tab — count of shipped hobby cards the user hasn't seen
  // yet (compares hobby_shipped_at vs users.last_seen_locker_at server-side).
  // Cleared by visiting /locker (LockerPage fires POST /api/locker/mark-seen).
  const [unseenLockerCount, setUnseenLockerCount] = useState(0);
  // Live break red dot — pulses when ANY break is currently `live`. Mirrors
  // the mobile bottom-nav so desktop viewers have a clear entry point into
  // the /live lobby (was previously mobile-only — fixed 2026-02-22 per user).
  const [liveBreakCount, setLiveBreakCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API}/live-breaks/?status=live`);
        if (!cancelled) setLiveBreakCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch { /* fail silent — the dot just won't show */ }
    };
    fetchLive();
    const id = setInterval(fetchLive, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    // Navigate FIRST so the current page unmounts cleanly before auth clears.
    navigate("/");
    setTimeout(() => logout(), 0);
  };

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => setTimeUpdate(t => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  // === WEBSOCKET CONNECTION FOR REAL-TIME NOTIFICATIONS ===
  useEffect(() => {
    if (!user || !token) {
      // Close existing connection if user logs out
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setWsConnected(false);
      }
      return;
    }

    // Create WebSocket connection - use /api prefix for ingress routing
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/notifications/${user.id}`);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected for real-time notifications');
      setWsConnected(true);
      
      // Send ping every 25 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
      
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'notification') {
          const notification = data.data;
          
          // Add to notifications list
          setNotifications(prev => {
            // Skip if it already exists or was dismissed on this device
            if (prev.some(n => n.id === notification.id)) {
              return prev;
            }
            if (loadNotifLS().dismissed.includes(notification.id)) {
              return prev;
            }
            return [notification, ...prev].slice(0, 50);
          });
          
          // Show toast notification
          setNewNotificationToast(notification);
          setTimeout(() => setNewNotificationToast(null), 6000);

          // Play appropriate sound + haptic based on notification type
          const t = notification.type || "";
          if (t.includes("offer_accepted") || t === "sale" || t === "purchase") {
            feedback.sale();
          } else if (t.includes("offer_rejected") || t.includes("offer_cancelled")) {
            feedback.offerRejected();
          } else if (t === "gift") {
            feedback.gift();
          } else if (t === "achievement" || t === "challenge_complete" || t === "challenge_reward") {
            feedback.achievement();
          } else if (t === "first_rip_rotation") {
            // Founder Edition SOLD OUT → next series LIVE. Use the achievement
            // chime to convey urgency + celebration without inventing a new
            // sound; matches the marketing-moment intent.
            feedback.achievement();
          } else if (t.includes("trade")) {
            feedback.trade();
          } else {
            feedback.notification();
          }
        }
      } catch (err) {
        // Ignore non-JSON messages (like 'pong')
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      if (ws.pingInterval) {
        clearInterval(ws.pingInterval);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setWsConnected(false);
    };

    // Cleanup on unmount or user change
    return () => {
      if (ws.pingInterval) {
        clearInterval(ws.pingInterval);
      }
      ws.close();
    };
  }, [user, token]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    lsRememberNotif("read", [notificationId]);
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (err) {
      // Even if the API fails, the device remembers it as read
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    lsRememberNotif("read", notifications.map(n => n.id));
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
  };

  // Fetch unseen shipping count — drives the red dot on the Locker nav tab.
  // Re-runs when the user navigates away from /locker (since LockerPage's
  // mount fires /mark-seen which would have reset the count to 0).
  useEffect(() => {
    if (!user || !token) { setUnseenLockerCount(0); return; }
    let cancelled = false;
    const fetchUnseen = async () => {
      try {
        const res = await axios.get(`${API}/locker/unseen-shipping-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setUnseenLockerCount(res.data?.count || 0);
      } catch (err) {
        // Silent — endpoint is non-critical, never spam the console
      }
    };
    fetchUnseen();
    // Lightweight poll every 60s so newly-shipped cards from admin Ship-Drop
    // surface without a manual refresh. 60s keeps server load negligible.
    const id = setInterval(fetchUnseen, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, token, location.pathname]);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Changelog announcements are one-time pushes — delete on click so the
    // bell auto-dismisses them rather than lingering as a read notification.
    if (notification.type === 'changelog' || notification.type === 'announcement' || notification.auto_dismiss_on_read) {
      lsRememberNotif("dismissed", [notification.id]);
      try {
        await axios.delete(`${API}/notifications/${notification.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      } catch (err) {
        // Fall back to marking as read if delete fails for any reason.
        if (!notification.read) {
          await markAsRead(notification.id);
        }
      }
    } else if (!notification.read) {
      await markAsRead(notification.id);
    }

    setShowNotifications(false);
    
    // Navigate based on action_url or notification type
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.type === 'announcement' || notification.type === 'changelog') {
      // Ad-hoc admin announcements with no action_url — just dismiss in place.
      return;
    } else if (notification.type === 'queue_ready' || notification.type === 'queue_joined') {
      navigate('/drops');
    } else if (notification.type === 'claim_approved' || notification.type === 'gift') {
      navigate('/locker');
    } else if (notification.type === 'certificate_available' || notification.type === 'certificate_approved' || notification.type === 'certificate_claimed' || notification.type === 'certificate_declined') {
      navigate('/locker');
    } else if (notification.type === 'kyc_approved' || notification.type === 'kyc_rejected' || notification.type === 'kyc_submitted') {
      navigate('/wallet');
    } else if (notification.type === 'sale' || notification.type === 'offer' || notification.type === 'purchase') {
      navigate('/wallet');
    } else if (notification.type === 'offer_message' || notification.type === 'offer_received' || notification.type === 'counter_offer' || notification.type === 'offer_accepted' || notification.type === 'offer_rejected' || notification.type === 'offer_cancelled') {
      navigate(notification.offer_id ? `/marketplace?offer=${notification.offer_id}` : '/marketplace');
    } else if (notification.type === 'challenge_complete' || notification.type === 'challenge_reward') {
      navigate('/locker');
    } else if (notification.type === 'achievement') {
      navigate('/profile');
    } else if (notification.type === 'price_update' || notification.type === 'price_alert') {
      navigate('/marketplace');
    } else if (notification.type?.startsWith('admin_') || notification.type === 'kyc_submission') {
      navigate('/admin');
    } else if (notification.type === 'shipment_update') {
      navigate('/locker');
    } else if (notification.type?.startsWith('blkpay_')) {
      // BLK PAY notifications - user goes to wallet, admin to admin panel
      navigate(notification.type === 'blkpay_request' ? '/admin' : '/wallet');
    } else if (notification.type === 'account_warning' || notification.type === 'account_suspended') {
      navigate('/profile');
    } else {
      // Default: go to locker for collectible-related notifications
      navigate('/locker');
    }
  };

  // Dismiss notification (delete from list)
  const dismissNotification = async (e, notificationId) => {
    e.stopPropagation();
    lsRememberNotif("dismissed", [notificationId]);
    // Remove from local state immediately — device remembers even if API fails
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      // Fallback to mark as read if delete fails
      try { await axios.post(`${API}/notifications/${notificationId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch { /* device memory covers it */ }
    }
  };

  // Clear all read notifications
  const clearAllRead = async () => {
    lsRememberNotif("dismissed", notifications.filter(n => n.read).map(n => n.id));
    setNotifications(prev => prev.filter(n => !n.read));
    try {
      await axios.delete(`${API}/notifications/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to clear notifications');
    }
  };

  // Fetch notifications - only for logged in users
  useEffect(() => {
    if (!user || !token) {
      setNotifications([]);
      return;
    }
    
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${API}/user/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Device safety net: never show anything this device already
        // dismissed, and keep locally-read items read — even if the
        // backend response is stale.
        const ls = loadNotifLS();
        const newNotifications = res.data
          .filter(n => !ls.dismissed.includes(n.id))
          .slice(0, 50)
          .map(n => (ls.read.includes(n.id) ? { ...n, read: true } : n));
        const newUnreadCount = newNotifications.filter(n => !n.read).length;
        
        // Show toast for new notifications
        if (newUnreadCount > prevUnreadCount && prevUnreadCount > 0) {
          const latestUnread = newNotifications.find(n => !n.read);
          if (latestUnread) {
            setNewNotificationToast(latestUnread);
            setTimeout(() => setNewNotificationToast(null), 5000);
          }
        }
        
        setPrevUnreadCount(newUnreadCount);
        setNotifications(newNotifications);
      } catch (err) {
        // Silently fail
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [user, token, prevUnreadCount]);

  // Desktop nav items. After the 2026-06 rename:
  //   - "Drops" links to /drops (was /play)
  //   - "PLAY" links to /play (was /challenges) — has a hover dropdown
  //     surfacing Sets + Series so the top bar isn't a 10-link wall.
  // Series + Sets are removed from the top level (still reachable via
  // the PLAY dropdown and via direct URLs).
  // 2026-06: "Sell" merged INTO "Scan AI" as a dropdown child and the
  // combined item moved one slot left (sits right after PLAY) so the
  // most-monetised entry point gets prime placement.
  const navItems = [
    { path: "/", label: "Home", icon: null },
    { path: "/drops", label: "Drops", icon: Zap },
    {
      path: "/play",
      label: "PLAY",
      icon: Trophy,
      children: [
        { path: "/sets", label: "Sets", icon: Layers },
        { path: "/series", label: "Series", icon: Crown },
      ],
    },
    {
      path: "/scan",
      label: "Scan AI",
      icon: ScanLine,
      isNew: true,
      children: [
        { path: "/sell", label: "Sell", icon: DollarSign },
      ],
    },
    { path: "/marketplace", label: "Swap Spot", icon: ArrowLeftRight },
    { path: "/live", label: "Live", icon: Radio },
    { path: "/locker", label: "Locker", icon: Gem },
    { path: "/verify", label: "EMERGE", icon: ShieldCheck },
    { path: "/events", label: "Events", icon: Zap, desktopHidden: true },
    { path: "/staking", label: "Lock In", icon: Lock, desktopHidden: true },
  ];
  
  // Items that go in Profile/Settings section (not main nav)
  const settingsItems = [
    { path: "/wallet", label: "Points", icon: Wallet },
    { path: "/calculator", label: "Calculator", icon: DollarSign },
  ];

  if (user?.is_admin) {
    // Host Dashboard is reachable from inside /admin (a button on the
    // Admin page). Keeping it out of the main nav avoids the redundant
    // "Host + Admin" side-by-side pair eating mobile nav real estate and
    // confusing the admin into thinking it's user-facing. Once we onboard
    // 3rd-party hosts (future P3), this gets gated by `is_host` and
    // surfaced again.
    navItems.push({ path: "/admin", label: "Admin", icon: Shield });
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  // Group look-alike notifications (same type + title) into one collapsed
  // row — e.g. 8 "Boost Expired" rows render as a single "8× Boost Expired".
  const groupedNotifications = (() => {
    const map = new Map();
    for (const n of notifications) {
      const key = `${n.type || ''}|${n.title || ''}`;
      if (!map.has(key)) map.set(key, { key, latest: n, items: [] });
      map.get(key).items.push(n);
    }
    return Array.from(map.values()).map(g => ({
      ...g,
      count: g.items.length,
      anyUnread: g.items.some(n => !n.read),
    }));
  })();

  // Open a group: mark every unread item read, then act on the newest one
  const handleGroupClick = (group) => {
    group.items
      .filter(n => !n.read && n.id !== group.latest.id)
      .forEach(n => markAsRead(n.id));
    handleNotificationClick(group.latest);
  };

  // X a group: dismiss every notification it collapses
  const dismissGroup = async (e, group) => {
    e.stopPropagation();
    const ids = group.items.map(n => n.id);
    lsRememberNotif("dismissed", ids);
    setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
    await Promise.allSettled(ids.map(id =>
      axios.delete(`${API}/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
    ));
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#12121e]/95 backdrop-blur-md border-b border-orange-500/20" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* 🔥 LEGENDARY match live alert — chime + flash + clickable pill */}
      <LegendaryLiveFlash />
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-12 md:h-14">
          {/* LEFT cluster: logo + shout-out ring locked together. Wrapping
              them in their own flex container means `justify-between` on
              the parent pushes the WHOLE cluster left as one unit, instead
              of treating the ring as a middle sibling and floating it to
              the centre of the navbar on mobile. */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              {/* BLK gradient gothic-B brand mark */}
              <img
                src="/images/blk-gradient-b-transparent.png"
                alt="BLK"
                className="w-9 h-9 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]"
              />
              <div className="font-title text-xl tracking-tight flex items-center gap-1">
                <span className="text-white">BLK</span>
                <span className="brand-gradient-text">PACKS</span>
              </div>
            </Link>

            {/* Celeb Shout-outs ring — renders null when there are no active
                shout-outs, so this slot is invisible until you upload your
                first clip. Visible on every page, signed in or out. Lives
                outside Live Break / Tournament real estate by design. */}
            <StoriesRing />
          </div>

          {/* Desktop Nav - Clean Navigation */}
          <div className="hidden md:flex items-center gap-1 flex-shrink-0">
            {navItems.filter(item => !item.desktopHidden).map((item) => {
              // Items with `children` render as a hover dropdown
              // (PLAY → Sets / Series). The parent stays clickable —
              // tapping it navigates to its own path, hovering reveals
              // the child links. Pattern keeps the top bar uncluttered
              // while preserving direct keyboard/click access to the
              // hub page.
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const isActive =
                location.pathname === item.path ||
                (hasChildren && item.children.some(c => location.pathname === c.path));
              if (hasChildren) {
                return (
                  <div key={item.path} className="relative group">
                    <Link
                      to={item.path}
                      className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 ${
                        isActive
                          ? "bg-orange-500/20 text-amber-400"
                          : "text-gray-400 hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                      <ChevronDown size={14} className="opacity-60 group-hover:rotate-180 transition-transform" />
                    </Link>
                    {/* Dropdown — `group-hover` keeps it open while the
                        cursor is over EITHER the trigger or the panel.
                        `pt-2` adds a hover-buffer so a small mouse gap
                        between trigger + panel doesn't dismiss it. */}
                    <div
                      className="absolute left-0 top-full pt-2 hidden group-hover:block z-40"
                      data-testid={`nav-${item.label.toLowerCase()}-dropdown`}
                    >
                      <div className="bg-[#12121e] border border-orange-500/20 rounded-xl shadow-2xl py-2 min-w-[160px] backdrop-blur-md">
                        {item.children.map(c => {
                          const ChildIcon = c.icon;
                          const childActive = location.pathname === c.path;
                          return (
                            <Link
                              key={c.path}
                              to={c.path}
                              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                                childActive
                                  ? "text-amber-400 bg-orange-500/10"
                                  : "text-gray-300 hover:text-white hover:bg-white/5"
                              }`}
                              data-testid={`nav-${c.label.toLowerCase()}`}
                            >
                              {ChildIcon && <ChildIcon size={14} />}
                              {c.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  location.pathname === item.path
                    ? "bg-orange-500/20 text-amber-400"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {item.label}
                {/* Live tab red dot — pulses red whenever a host is currently
                    streaming. Mirrors the mobile bottom-nav so desktop users
                    have the same visual cue. Fixed 2026-02-22. */}
                {item.path === "/live" && liveBreakCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex items-center justify-center"
                    data-testid="live-active-dot"
                    aria-label={`${liveBreakCount} live break${liveBreakCount === 1 ? "" : "s"} happening now`}
                    title={`${liveBreakCount} live break${liveBreakCount === 1 ? "" : "s"} happening now`}
                  >
                    <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-red-500 opacity-60 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.85)]" />
                  </span>
                )}
                {/* Locker tab red dot — only renders on /locker entry when
                    the user has at least one shipped hobby card they haven't
                    seen yet. Subtle 8px dot in the top-right corner with a
                    soft pulse ring so it catches the eye without screaming. */}
                {item.path === "/locker" && unseenLockerCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex items-center justify-center"
                    data-testid="locker-unseen-dot"
                    aria-label={`${unseenLockerCount} unseen shipping update${unseenLockerCount === 1 ? "" : "s"}`}
                    title={`${unseenLockerCount} new shipping update${unseenLockerCount === 1 ? "" : "s"}`}
                  >
                    <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-orange-500 opacity-60 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 brand-gradient-bg shadow-[0_0_6px_rgba(249,115,22,0.7)]" />
                  </span>
                )}
              </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {/* Notification Bell - Only show for logged in users */}
            {user && (
            <div className="relative">
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative p-2 rounded-lg transition-all ${unreadCount > 0 ? 'text-red-500 bg-red-500/10 animate-pulse' : 'text-gray-400 hover:text-white'}`}
                data-testid="notification-bell"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notifications Dropdown - Professional Design */}
              {showNotifications && (
                <>
                  {/*
                    Click-outside backdrop — must cover the WHOLE viewport so
                    taps anywhere outside the dropdown close it. The navbar
                    has `backdrop-blur-md`, which (per the CSS containing-block
                    spec) anchors `position: fixed` descendants to the navbar
                    instead of the viewport. Portaling the backdrop to
                    document.body sidesteps that and lets `fixed inset-0`
                    truly cover the page.
                  */}
                  {typeof document !== "undefined" && document.body &&
                    createPortal(
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowNotifications(false)}
                      />,
                      document.body
                    )
                  }
                  
                  <div className="absolute right-0 mt-2 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-700 bg-gray-800/50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bell size={18} className="text-amber-400" />
                        <span className="text-white font-bold">Notifications</span>
                        {unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount} new</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button 
                            onClick={markAllAsRead}
                            className="text-gray-400 hover:text-amber-400 text-xs"
                          >
                            Mark all read
                          </button>
                        )}
                        {notifications.filter(n => n.read).length > 0 && (
                          <button 
                            onClick={clearAllRead}
                            className="text-gray-400 hover:text-red-400 text-xs"
                          >
                            Clear read
                          </button>
                        )}
                        <button 
                          onClick={() => setShowNotifications(false)}
                          className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-700"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Notification list */}
                    <div className="max-h-[400px] overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-gray-500 text-center">
                          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Bell size={24} className="opacity-50" />
                          </div>
                          <p className="font-medium">No notifications yet</p>
                          <p className="text-xs mt-1">You're all caught up!</p>
                        </div>
                      ) : (
                        groupedNotifications.map((g) => {
                          const n = g.latest;
                          return (
                          <div 
                            key={g.key + n.id} 
                            className={`p-4 border-b border-gray-800/50 hover:bg-gray-800/50 cursor-pointer transition-all relative group ${
                              g.anyUnread ? 'bg-gray-800/30' : ''
                            }`}
                            onClick={() => handleGroupClick(g)}
                            data-testid={`notification-group-${g.key.replace(/[^a-zA-Z0-9_-]/g, '')}`}
                          >
                            {/* Unread indicator */}
                            {g.anyUnread && (
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-500 to-orange-500" />
                            )}
                            
                            <div className="flex gap-3">
                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                n.type === 'kyc_approved' ? 'bg-green-500/20 text-green-500' :
                                n.type === 'kyc_rejected' ? 'bg-red-500/20 text-red-500' :
                                n.type === 'kyc_submitted' ? 'bg-orange-500/20 text-amber-400' :
                                n.type === 'gift' ? 'bg-amber-500/20 text-amber-500' :
                                n.type === 'sale' ? 'bg-green-500/20 text-green-500' :
                                n.type === 'queue_ready' ? 'bg-blue-500/20 text-blue-500' :
                                'bg-gray-700 text-gray-400'
                              }`}>
                                {n.type === 'kyc_approved' ? <Check size={18} /> :
                                 n.type === 'kyc_rejected' ? <X size={18} /> :
                                 n.type === 'gift' ? <Gift size={18} /> :
                                 n.type === 'sale' ? <DollarSign size={18} /> :
                                 n.type === 'queue_ready' ? <Play size={18} /> :
                                 <Bell size={18} />}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className={`text-sm font-medium flex items-center gap-2 ${g.anyUnread ? 'text-white' : 'text-gray-300'}`}>
                                    {n.title}
                                    {g.count > 1 && (
                                      <span className="bg-orange-500/20 text-orange-400 text-[10px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid="notification-group-count">
                                        ×{g.count}
                                      </span>
                                    )}
                                  </p>
                                  {/* Dismiss button — clears the whole group */}
                                  <button 
                                    onClick={(e) => dismissGroup(e, g)}
                                    className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 p-1 rounded transition-opacity"
                                    title={g.count > 1 ? `Dismiss all ${g.count}` : "Dismiss"}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                                <p className="text-gray-500 text-xs mt-1 line-clamp-2">
                                  {g.count > 1 ? `${n.message} · +${g.count - 1} more like this` : n.message}
                                </p>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-gray-600 text-xs">
                                    {new Date(n.created_at).toLocaleDateString()} at {new Date(n.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                  {n.action_label && (
                                    <span className="text-amber-400 text-xs font-medium hover:text-amber-300">
                                      {n.action_label} →
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            )}

            {/* New Notification Toast Popup */}
            {/*
              Portaled to <body> so `fixed top-20 right-4` resolves against
              the actual viewport — not the navbar's containing block
              (the navbar has `backdrop-blur-md` which would otherwise
              anchor this toast to the nav's bounds and break placement
              on mobile / when the navbar resizes).
            */}
            {newNotificationToast && typeof document !== "undefined" && document.body &&
              createPortal(
                <div className="fixed top-20 right-4 z-[100] animate-slide-in-right">
                <div className="bg-gray-900 border border-orange-500/50 rounded-xl shadow-2xl p-4 max-w-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bell className="text-amber-400" size={18} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-bold text-sm">{newNotificationToast.title}</p>
                      <p className="text-gray-400 text-xs mt-1 line-clamp-2">{newNotificationToast.message}</p>
                      <div className="flex gap-2 mt-3">
                        <button 
                          onClick={() => {
                            handleNotificationClick(newNotificationToast);
                            setNewNotificationToast(null);
                          }}
                          className="bg-orange-500 hover:bg-amber-400 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                        >
                          View
                        </button>
                        <button 
                          onClick={() => setNewNotificationToast(null)}
                          className="text-gray-400 hover:text-white text-xs px-3 py-1.5"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    <button 
                      onClick={() => setNewNotificationToast(null)}
                      className="text-gray-500 hover:text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>,
                document.body
              )
            }

            {user ? (
              <>
                {/* Only surface on truly wide screens (>=1536px); admin
                    navbar has many icons already and breaks below that. */}
                <GetAppButton size="sm" testId="navbar-get-app-desktop-user" className="hidden 2xl:inline-flex" />
                <div className="text-sm flex items-center gap-1 whitespace-nowrap">
                  <span className="text-gray-400 hidden sm:inline">Pack Points:</span>
                  <span className="text-amber-400 font-bold">{user.balance >= 1000 ? `${(user.balance/1000).toFixed(1)}k` : user.balance?.toFixed(0)}</span>
                  <span className="text-amber-400 text-xs">pts</span>
                </div>
                <Link to="/wallet" className="text-gray-400 hover:text-green-500 transition-colors" title="Wallet">
                  <Wallet size={20} />
                </Link>
                <Link to="/calculator" className="text-gray-400 hover:text-amber-400 transition-colors" title="Calculator" data-testid="navbar-calculator-link">
                  <Calculator size={20} />
                </Link>
                <Link to="/profile" className="text-gray-400 hover:text-amber-400" title="Profile">
                  <User size={20} />
                </Link>
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="text-gray-400 hover:text-white"
                  title="Sign Out"
                  data-testid="navbar-logout-btn"
                >
                  <LogOut size={20} />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <GetAppButton size="sm" testId="navbar-get-app-desktop" className="hidden lg:inline-flex" />
                <Link 
                  to="/login" 
                  className="text-gray-300 hover:text-white font-medium text-sm px-3 py-1.5 rounded-lg transition-all hover:bg-white/5"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="bg-orange-500 hover:bg-amber-400 text-white font-bold text-sm px-4 py-1.5 rounded-lg transition-all"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => { haptic.light(); setMenuOpen(!menuOpen); }}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - NBA Top Shot Style */}
      {menuOpen && (
        <div className="md:hidden absolute left-0 right-0 top-full bg-[#0d0d0f] border-t border-gray-800/50 overflow-y-auto overscroll-contain" style={{WebkitOverflowScrolling: 'touch', maxHeight: 'calc(100vh - 64px - env(safe-area-inset-top, 0px))'}}>
          <div className="px-4 py-4 pb-24">

            {/* GET THE APP — top of mobile menu so it's the first CTA users see */}
            <div className="mb-5">
              <GetAppButton size="lg" fullWidth testId="mobile-menu-get-app" />
            </div>

            {/* Sign In CTA for Non-Logged In Users - TOP PRIORITY */}
            {!user && (
              <div className="mb-6">
                <h2 className="text-white text-xl font-bold mb-3">Welcome to BLK PACKS™</h2>
                <Link
                  to="/login"
                  onClick={() => { haptic.medium(); setMenuOpen(false); }}
                  className="block w-full px-4 py-3.5 bg-orange-500 text-white font-bold text-center rounded-xl hover:bg-amber-400 transition-all active:scale-[0.97] mb-3"
                  data-testid="mobile-sign-in-btn"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => { haptic.medium(); setMenuOpen(false); }}
                  className="block w-full px-4 py-3 bg-gray-800 text-white font-bold text-center rounded-xl hover:bg-gray-700 transition-all active:scale-[0.97] border border-gray-700 mb-3"
                  data-testid="mobile-register-btn"
                >
                  Create Account
                </Link>
                <Link
                  to="/forgot-password"
                  onClick={() => { haptic.light(); setMenuOpen(false); }}
                  className="block w-full text-amber-400 text-center text-sm hover:underline"
                  data-testid="mobile-forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </div>
            )}
            
            {/* Time-Based Greeting */}
            {user && (
              <div className="mb-4" data-testid="greeting-section">
                <h2 className="text-white text-xl font-bold">
                  {getTimeBasedGreeting()}, <span className="brand-gradient-text font-black">{user.username || user.email?.split('@')[0] || 'Collector'}</span>
                </h2>
              </div>
            )}
            
            {/* User Points Card */}
            {user && (
              <div className="bg-gradient-to-br from-gray-900 to-gray-900/50 rounded-2xl p-4 mb-6 border border-gray-800/50">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-500 text-xs uppercase tracking-wider block">Pack Points</span>
                    <div className="text-white font-black text-2xl mt-1">
                      {user.balance >= 10000 ? `${(user.balance/1000).toFixed(1)}k` : user.balance?.toFixed(0)}
                      <span className="text-sm text-gray-400 ml-1">pts</span>
                    </div>
                  </div>
                  <Link 
                    to="/wallet" 
                    onClick={() => setMenuOpen(false)}
                    className="bg-orange-500 hover:bg-amber-400 text-white text-xs font-bold px-4 py-2 rounded-full flex-shrink-0 whitespace-nowrap"
                  >
                    Get More
                  </Link>
                </div>
                {user.email && (
                  <div className="mt-3 pt-3 border-t border-gray-800/50">
                    <span className="text-gray-500 text-xs block">Account</span>
                    <p className="text-gray-400 text-sm truncate">{user.email}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Main Navigation */}
            <div className="space-y-1">
              {navItems.filter(item => item.path !== "/").map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => { setMenuOpen(false); haptic.medium(); }}
                  // iOS touch hardening — kills the "tap PLAY, highlight
                  // jumps to Swap Spot below" bug (2026-06):
                  // 1. WebkitTapHighlightColor=transparent → removes iOS's
                  //    default grey-flash that overlapped neighboring rows.
                  // 2. touchAction=manipulation → drops the 300ms tap delay
                  //    so the menu closes in the same frame as the tap,
                  //    eliminating the post-tap "ghost highlight" frame.
                  // 3. setMenuOpen(false) is now called BEFORE haptic so the
                  //    drawer closes in the same React batch as navigation.
                  // 4. The Tailwind `hoverOnlyWhenSupported` future flag
                  //    (tailwind.config.js) ensures `hover:` classes never
                  //    fire on touch devices in the first place.
                  style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all active:scale-[0.97] ${
                    location.pathname === item.path
                      ? "bg-orange-500/10 text-white"
                      : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                  }`}
                >
                  {item.icon && <item.icon size={20} className={location.pathname === item.path ? "text-amber-400" : ""} />}
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-800/50 my-6" />
            
            {/* Explore Section - EMERGE Public Ledger (primary) + Activity Feed */}
            <div className="mb-6">
              <span className="text-gray-600 text-xs uppercase tracking-wider px-4 mb-2 block">Explore</span>
              <Link
                to="/verify"
                onClick={() => { haptic.medium(); setMenuOpen(false); }}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all active:scale-[0.97] ${
                  location.pathname.startsWith("/verify")
                    ? "bg-emerald-500/10 text-white"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                }`}
                data-testid="emerge-menu-link"
              >
                <ShieldCheck size={20} className={location.pathname.startsWith("/verify") ? "text-emerald-400" : ""} />
                <span className="font-medium">EMERGE · Public Ledger</span>
              </Link>
              <Link
                to="/activity"
                onClick={() => { haptic.medium(); setMenuOpen(false); }}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all active:scale-[0.97] ${
                  location.pathname === "/activity"
                    ? "bg-orange-500/10 text-white"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                }`}
                data-testid="activity-feed-menu-link"
              >
                <Activity size={20} className={location.pathname === "/activity" ? "text-amber-400" : ""} />
                <span className="font-medium">Community Activity</span>
              </Link>
            </div>
            
            {/* Settings Section - NBA Top Shot Style */}
            <div className="space-y-1">
              {user && (
                <>
                  <Link
                    to="/calculator"
                    onClick={() => { haptic.medium(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-amber-400 hover:bg-amber-500/10 transition-all active:scale-[0.97]"
                    data-testid="menu-calculator-link"
                  >
                    <Calculator size={20} />
                    <span className="font-medium">Calculator</span>
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => { haptic.medium(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-400 hover:bg-gray-800/50 hover:text-white transition-all active:scale-[0.97]"
                  >
                    <User size={20} />
                    <span className="font-medium">Profile Settings</span>
                  </Link>
                  
                  
                  <Link
                    to="/wallet"
                    onClick={() => { haptic.medium(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-400 hover:bg-gray-800/50 hover:text-white transition-all active:scale-[0.97]"
                  >
                    <Wallet size={20} />
                    <span className="font-medium">Pack Points</span>
                  </Link>
                  <Link
                    to="/profile#refer"
                    onClick={() => { haptic.medium(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-green-400 hover:bg-green-500/10 transition-all active:scale-[0.97]"
                  >
                    <Users size={20} />
                    <span className="font-medium">Refer a Friend</span>
                  </Link>
                  <Link
                    to="/claim"
                    onClick={() => { haptic.medium(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-amber-400 hover:bg-amber-500/10 transition-all active:scale-[0.97]"
                  >
                    <Package size={20} />
                    <span className="font-medium">Claim Your Card</span>
                  </Link>
                  <Link
                    to="/locker?filter=listed"
                    onClick={() => { haptic.medium(); setMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-orange-400 hover:bg-orange-500/10 transition-all active:scale-[0.97]"
                  >
                    <Rocket size={20} />
                    <span className="font-medium">Promote & Boost</span>
                  </Link>
                  
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => { haptic.medium(); setMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-amber-400 hover:bg-amber-500/10 transition-all active:scale-[0.97]"
                    >
                      <Shield size={20} />
                      <span className="font-medium">Admin Panel</span>
                    </Link>
                  )}
                </>
              )}
              
              {/* Notifications Link */}
              {user && (
                <button
                  onClick={() => { haptic.medium(); setShowNotifications(true); setMenuOpen(false); }}
                  className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-gray-400 hover:bg-gray-800/50 hover:text-white transition-all active:scale-[0.97]"
                >
                  <div className="flex items-center gap-3">
                    <Bell size={20} />
                    <span className="font-medium">Notifications</span>
                  </div>
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>
              )}
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-800/50 my-6" />
            
            {/* Company Links */}
            <div className="space-y-1 mb-6">
              <span className="text-gray-600 text-xs uppercase tracking-wider px-4 mb-2 block">Company</span>
              <Link
                to="/about"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">About Us</span>
              </Link>
              <Link
                to="/contact"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Contact</span>
              </Link>
              <Link
                to="/faq"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">FAQ</span>
              </Link>
              <Link
                to="/shipping"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-white transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Shipping & Returns</span>
              </Link>
            </div>
            
            {/* Legal Links - Clean List */}
            <div className="space-y-1">
              <span className="text-gray-600 text-xs uppercase tracking-wider px-4 mb-2 block">Legal</span>
              <Link
                to="/terms"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-500 hover:text-gray-300 transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Terms of Service</span>
              </Link>
              <Link
                to="/privacy"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-500 hover:text-gray-300 transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Privacy Policy</span>
              </Link>
              <Link
                to="/copyright"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-500 hover:text-gray-300 transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Copyright</span>
              </Link>
              <Link
                to="/licensing"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-500 hover:text-gray-300 transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Licensing</span>
              </Link>
              <Link
                to="/partnerships"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-500 hover:text-gray-300 transition-all active:scale-[0.97]"
              >
                <FileText size={18} />
                <span className="text-sm">Partnerships</span>
              </Link>
              <Link
                to="/report"
                onClick={() => { haptic.light(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-500 hover:text-gray-300 transition-all active:scale-[0.97]"
              >
                <AlertTriangle size={18} />
                <span className="text-sm">Report Abuse</span>
              </Link>
            </div>
            
            {/* Divider */}
            <div className="border-t border-gray-800/50 my-6" />
            
            {/* Log Out (only for logged in users) */}
            {user && (
              <button
                onClick={() => {
                  haptic.heavy();
                  setMenuOpen(false);
                  // Open the confirm dialog instead of logging out immediately.
                  // Accidental mobile taps were wiping sessions mid-game.
                  setShowLogoutConfirm(true);
                }}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-red-400 hover:bg-red-500/10 w-full transition-all active:scale-[0.97]"
                data-testid="mobile-logout-btn"
              >
                <LogOut size={20} />
                <span className="font-medium">Log Out</span>
              </button>
            )}
            
            {/* App Version — live from CapacitorUpdater.current() on native */}
            <div className="text-center mt-6">
              <AppVersionBadge />
            </div>
          </div>
        </div>
      )}

      {/* Greeting Banner — PST time, below navbar (hidden on EMERGE public ledger) */}
      {user && !menuOpen && !location.pathname.startsWith("/verify") && (
        <div className="border-t border-white/[0.04] bg-[#0a0a12]/80">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <h2 className="text-white font-bold text-sm" data-testid="greeting-banner">
              {getTimeBasedGreeting()}, <span className="brand-gradient-text font-black">{user.username || user.email?.split('@')[0] || 'Collector'}</span>
            </h2>
            <span className="text-gray-600 text-[10px] font-mono" data-testid="pst-time">{getPSTTime()}</span>
          </div>
        </div>
      )}
      {/* Sign-out confirmation — protects against accidental logout taps */}
      {showLogoutConfirm && (
        <ConfirmModal
          title="Sign out of BLK PACKS?"
          message="You'll need to sign back in to access your locker, packs, and tournament progress."
          confirmLabel="Sign Out"
          cancelLabel="Stay Signed In"
          variant="warning"
          onConfirm={confirmLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </nav>
  );
};

export default Navbar;
